import type { MktHerkunftGruppe, MktQuelle, MktUebersicht, MktUebersichtKpi } from '@portal/contracts';
import { istVorlaeufig, tagAusDeutsch, tagDeutsch } from './marketing-ui.js';

/**
 * #901b Marketing-Übersicht (PD-Layoutkonzept #901): reine Anzeige-Hilfen — die API liefert alle Werte fertig berechnet
 * (KPIs mit Δ, Verlauf mit Lücken, Herkunft-Gruppen, …). Hier nur Zeitraumwahl, Formatierung, Δ-Darstellung, Beschriftungen.
 */

// ── Zeitraum ───────────────────────────────────────────────────────────────────────────────────────────────────────
export type UeModus = 'gestern' | '7' | '30' | 'frei';
export const UE_MODI: readonly { modus: UeModus; label: string }[] = [
  { modus: 'gestern', label: 'Gestern' },
  { modus: '7', label: '7 Tage' },
  { modus: '30', label: '30 Tage' },
  { modus: 'frei', label: 'Freier Zeitraum' },
];
export type UePreset = '7' | '30' | '91' | '364' | 'gesamt';
export const UE_PRESETS: readonly { preset: UePreset; label: string }[] = [
  { preset: '7', label: '7 T' },
  { preset: '30', label: '30 T' },
  { preset: '91', label: 'Quartal' },
  { preset: '364', label: 'Jahr' },
  { preset: 'gesamt', label: 'Gesamt' },
];
export interface UeZeitraum { von: string; bis: string }

const TAG_MS = 86_400_000;
export const plusTage = (iso: string, n: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * TAG_MS).toISOString().slice(0, 10);
export const tageZwischen = (von: string, bis: string) => Math.round((Date.parse(`${bis}T00:00:00Z`) - Date.parse(`${von}T00:00:00Z`)) / TAG_MS) + 1;

/** Segment Gestern (= gewählter Tag) / 7 Tage / 30 Tage, jeweils bis gestern */
export function segmentZeitraum(modus: Exclude<UeModus, 'frei'>, gestern: string, tag = gestern): UeZeitraum {
  if (modus === 'gestern') return { von: tag, bis: tag };
  return { von: plusTage(gestern, -(Number(modus) - 1)), bis: gestern };
}

/** Preset im freien Zeitraum: rollierend bis gestern; Gesamt ab Historienbeginn (`historieAb`) */
export function presetZeitraum(preset: UePreset, gestern: string, historieAb: string | null): UeZeitraum | string {
  if (preset === 'gesamt') {
    if (!historieAb) return 'Noch keine Werte vorhanden.';
    return { von: historieAb <= gestern ? historieAb : gestern, bis: gestern };
  }
  return { von: plusTage(gestern, -(Number(preset) - 1)), bis: gestern };
}

/** Von/Bis aus TT.MM.JJJJ prüfen; Fehlertext oder Zeitraum */
export function freiPruefen(vonText: string, bisText: string, gestern: string, historieAb: string | null = null): UeZeitraum | string {
  const von = tagAusDeutsch(vonText);
  const bis = tagAusDeutsch(bisText);
  if (!von || !bis) return 'Bitte gültige Daten als TT.MM.JJJJ eingeben.';
  if (von > bis) return 'Von muss vor oder gleich Bis liegen.';
  if (bis > gestern) return `Bis darf höchstens gestern sein (${tagDeutsch(gestern)}).`;
  if (historieAb && bis < historieAb) return `Für diesen Zeitraum liegen noch keine Werte vor (ab ${tagDeutsch(historieAb)}).`;
  return { von, bis };
}

/** Tagesfeld im Modus „Gestern“: beliebiger gültiger Tag ≤ gestern */
export function tagPruefen(text: string, gestern: string): string | { tag: string } {
  const tag = tagAusDeutsch(text);
  if (!tag) return 'Bitte ein gültiges Datum als TT.MM.JJJJ eingeben.';
  if (tag > gestern) return `Spätestens ${tagDeutsch(gestern)} (gestern).`;
  return { tag };
}

/** Zeile unter dem Kopf: „04.11.2026 · gestern“ bzw. „06.10.2026 – 04.11.2026 · 30 Tage“ */
export function zeitraumText(z: UeZeitraum, gestern: string): string {
  if (z.von === z.bis) return `${tagDeutsch(z.bis)}${z.bis === gestern ? ' · gestern' : ''}`;
  return `${tagDeutsch(z.von)} – ${tagDeutsch(z.bis)} · ${tageZwischen(z.von, z.bis)} Tage`;
}
/** Badge „vorläufig bis T+3“, wenn der Zeitraum in den Nachlauf reicht */
export const zeitraumVorlaeufig = (z: UeZeitraum, jetzt = new Date()) => istVorlaeufig(z.bis, jetzt);

/** Vergleichs-Caption direkt über den Kacheln (P7) */
export function vergleichText(v: MktUebersicht['vergleich']): string {
  if (!v) return 'kein Vergleichszeitraum';
  if (v.art === 'vorwochentag') return `vs. ${tagDeutsch(v.bis)} · gleicher Wochentag`;
  return `vs. ${tagDeutsch(v.von)}–${tagDeutsch(v.bis)} · vorherige Periode`;
}

// ── Formatierung ─────────────────────────────────────────────────────────────────────────────────────────────────────
const ZAHL = new Intl.NumberFormat('de-DE');
const EURO0 = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const EURO2 = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const DEZ1 = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const DEZ2 = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nbsp = (s: string) => s.replace(/\s/g, ' ');

export type UeFormat = 'zahl' | 'euro' | 'euro2' | 'faktor' | 'prozent';
/** null → „—“; euro ohne Cent (Kacheln), euro2 mit Cent (Ø Bestellwert); prozent = Wert bereits in % (wie API-Conversion) */
export function wertText(v: number | null | undefined, format: UeFormat): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  switch (format) {
    case 'euro': return nbsp(EURO0.format(v));
    case 'euro2': return nbsp(EURO2.format(v));
    case 'faktor': return `${DEZ2.format(v)} ×`;
    case 'prozent': return `${DEZ1.format(v)} %`;
    default: return ZAHL.format(v);
  }
}
/** Kachelwert: Hinweis (z. B. „nicht verfügbar“) statt erfundener Zahl */
export function kpiWert(k: Pick<MktUebersichtKpi, 'wert' | 'format' | 'hinweis'>): { text: string; istHinweis: boolean } {
  if (k.wert === null) return { text: k.hinweis ?? '—', istHinweis: k.hinweis !== null };
  return { text: wertText(k.wert, k.format), istHinweis: false };
}

export interface UeDelta { ton: 'positiv' | 'negativ' | 'neutral'; pfeil: string; text: string; sr: string }
/** Δ % mit Vorzeichen, Pfeil und Ton; `neutral` (Kosten) nie als Erfolg/Misserfolg; null → „— kein Vergleich“ */
export function deltaUe(d: number | null, neutral = false): UeDelta {
  if (d === null || Number.isNaN(d)) return { ton: 'neutral', pfeil: '', text: '— kein Vergleich', sr: 'kein Vergleichswert' };
  const pfeil = d > 0 ? '↑' : d < 0 ? '↓' : '→';
  const zahl = `${d > 0 ? '+' : d < 0 ? '−' : '±'}${DEZ1.format(Math.abs(d))} %`;
  const ton = neutral || d === 0 ? 'neutral' : d > 0 ? 'positiv' : 'negativ';
  const wort = d > 0 ? 'mehr' : d < 0 ? 'weniger' : 'unverändert';
  return { ton, pfeil, text: zahl, sr: d === 0 ? 'unverändert gegenüber dem Vergleich' : `${DEZ1.format(Math.abs(d))} Prozent ${wort} als im Vergleich` };
}

// ── Herkunft ─────────────────────────────────────────────────────────────────────────────────────────────────────────
/** Nur belegte Gruppen (s7 > 0 oder s30 > 0), Reihenfolge der API */
export const herkunftBelegt = (g: readonly MktHerkunftGruppe[]) => g.filter((x) => x.s7 > 0 || x.s30 > 0);
/** Anteil 0–1 → „31,2 %“ */
export const anteilText = (a: number | null | undefined) => (a === null || a === undefined ? '—' : `${DEZ1.format(a * 100)} %`);

// ── Verlauf ──────────────────────────────────────────────────────────────────────────────────────────────────────────
export const istWochenende = (iso: string) => [0, 6].includes(new Date(`${iso}T00:00:00Z`).getUTCDay());
/** runde Achsen-Obergrenze (Sitzungen auf 200er, Umsatz auf 1.000er) */
export function achsenMax(werte: readonly (number | null)[], metrik: 'sessions' | 'umsatz'): number {
  const schritt = metrik === 'umsatz' ? 1000 : 200;
  const max = Math.max(1, ...werte.map((v) => v ?? 0));
  return Math.ceil(max / schritt) * schritt;
}
const KURZ = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
/** aria-live-Zeile einer Säule: „Mi., 04.11.2026 · 812 Sitzungen“ / „… · keine Werte geliefert“ */
export function saeuleText(tag: string, v: number | null, metrik: 'sessions' | 'umsatz', bis: string): string {
  const datum = KURZ.format(new Date(`${tag}T00:00:00Z`));
  const wert = v === null ? 'keine Werte geliefert' : metrik === 'umsatz' ? wertText(v, 'euro') : `${wertText(v, 'zahl')} Sitzungen`;
  return `${datum} · ${wert}${tag === bis && v !== null && istVorlaeufig(tag) ? ' · vorläufig' : ''}`;
}
export const tagTTMM = (iso: string) => tagDeutsch(iso).slice(0, 6);

// ── Quellen ──────────────────────────────────────────────────────────────────────────────────────────────────────────
const LADEZEIT = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
export function geladenText(datenstand: string | null): string {
  if (!datenstand) return 'Noch nicht geladen';
  const d = new Date(datenstand);
  return Number.isNaN(d.getTime()) ? 'Ladezeit unbekannt' : `Geladen ${LADEZEIT.format(d)}`;
}
export const werteBisText = (q: Pick<MktQuelle, 'letzterTag'>) => (q.letzterTag ? `Werte bis ${tagDeutsch(q.letzterTag)}` : 'Noch keine Werte');
/** Quellen mit Messwerten (Karten) vs. wartende Plattformen (Freigabe-Liste) */
export const quellenAngebunden = (q: readonly MktQuelle[]) => q.filter((x) => x.status !== 'wartet_freigabe');
export const quellenWartend = (q: readonly MktQuelle[]) => q.filter((x) => x.status === 'wartet_freigabe');
export const plattformenWartenText = (n: number) => `${n} ${n === 1 ? 'Plattform wartet' : 'Plattformen warten'} auf Freigabe · Checkliste ansehen`;

/** SEO-Positionsänderung (kleiner = besser): „↑ +8“ / „↓ −3“ / „neu“ */
export function seoBewegung(vorher: number | null, jetzt: number | null): { ton: 'positiv' | 'negativ' | 'neutral'; text: string; sr: string } {
  if (vorher === null) return { ton: 'neutral', text: 'neu', sr: 'neu beobachtet' };
  if (jetzt === null) return { ton: 'neutral', text: 'nicht im Ausschnitt', sr: 'nicht mehr im Ausschnitt' };
  const d = vorher - jetzt;
  if (d === 0) return { ton: 'neutral', text: '→ ±0', sr: 'Position unverändert' };
  return d > 0 ? { ton: 'positiv', text: `↑ +${d}`, sr: `${d} Positionen besser` } : { ton: 'negativ', text: `↓ −${-d}`, sr: `${-d} Positionen schlechter` };
}
